import { apiErrorMessage } from '@agos/api-client'
import { useGetSettingsPermissionsEmployees, usePutSettingsPermissionsEmployeeId } from '@agos/api-client/auth'
import {
  baseSentence,
  isRoleBase,
  ROLE_BASES,
  roleLabel,
  T,
  tt,
  useModalFocus,
  type RoleBase,
  type RoleColor,
} from '@agos/shared'
import { BUTTON_PRIMARY, BUTTON_SECONDARY, cn, Spinner, useToast } from '@agos/ui'
import { AlertTriangle } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import {
  useAssignRole,
  useCreateRole,
  useRoleOptions,
  type RolesOverview,
  type RoleStartKind,
  type RoleView,
} from '../../roles/roles-api'
import { displayParentOf, overridePayload, permissionLabel } from '../permission-matrix'
import {
  grantsTheRoleCovers,
  optionConsequences,
  orderRoles,
  refusalText,
  roleNameOf,
  roleNameProblem,
  roleNameProblemText,
  roleWarnings,
  withoutCoveredGrants,
} from '../role-model'
import { ColorPicker } from './RoleEditor'

/** Where *Nová rola* opens from: a blank form, or *Duplikovať* on a role. */
export type NewRoleSeed =
  | { kind: 'blank' }
  | { kind: 'copy'; fromKey: string; name: string; baseRole: string }

/**
 * *Nová rola* - `FRONTEND.md` §9.752, the main administrator's alone (`POST /settings/roles`, §10A.408's R4).
 *
 * **The base is chosen here and never changes** (§10A.407), so it is four cards each saying what working as it means,
 * and nothing is pre-selected: a role nobody decided the base of is a role built on a default. *Začať od* is where its
 * rights start - the base's recommended set, a copy of another role, a person's menu (never everything), or nothing.
 *
 * **Started from a person, it then offers to move that person in**, with what the move would do - the server's own
 * options answer: whether it signs them out, what the server would stop allowing them (an administrator's pass does
 * not come with a copy of their menu), and a ticked *Zrušiť výnimky, ktoré nová rola už obsahuje*, written through the
 * existing exceptions endpoint. That is the design's path from *an administrator with four hand-ticked denials* to *a
 * dispatcher*.
 *
 * `useModalFocus` and one `data-dialog-cancel`, as every overlay in the manager (§9.477, §9.489).
 */
export function NewRoleDialog({
  seed,
  overview,
  onClose,
  onCreated,
}: {
  seed: NewRoleSeed
  overview: RolesOverview
  onClose: () => void
  onCreated: (role: RoleView) => void
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  const create = useCreateRole()
  const [name, setName] = useState(seed.kind === 'copy' ? seed.name : '')
  const [base, setBase] = useState<RoleBase | null>(
    seed.kind === 'copy' && isRoleBase(seed.baseRole) ? seed.baseRole : null,
  )
  const [startKind, setStartKind] = useState<RoleStartKind>(seed.kind === 'copy' ? 'role' : 'defaults')
  const [fromRole, setFromRole] = useState(seed.kind === 'copy' ? seed.fromKey : '')
  const [fromPerson, setFromPerson] = useState(0)
  const [color, setColor] = useState<RoleColor | null>(null)
  const [description, setDescription] = useState('')
  const [failure, setFailure] = useState('')
  const [created, setCreated] = useState<RoleView | null>(null)
  const [moving, setMoving] = useState(false)

  const busy = create.isPending || moving
  const close = () => (created === null ? onClose() : onCreated(created))
  const { onKeyDown } = useModalFocus(true, panelRef, busy ? null : close)

  const roles = orderRoles(overview.roles).filter((role) => role.key !== 'main_admin')
  const people = overview.people.filter((person) => person.systemRole !== 'main_admin')
  const problem = roleNameProblem(name, overview.roles)
  const startReady =
    (startKind !== 'role' || fromRole !== '') && (startKind !== 'person' || fromPerson > 0)
  const ready = problem === null && base !== null && startReady && !busy

  const submit = () => {
    if (base === null) return
    setFailure('')
    create.mutate(
      {
        data: {
          name: name.trim(),
          baseRole: base,
          description: description.trim() === '' ? null : description.trim(),
          color,
          start: {
            kind: startKind,
            key: startKind === 'role' ? fromRole : null,
            employeeId: startKind === 'person' ? fromPerson : null,
          },
        },
      },
      {
        onSuccess: (role: RoleView) => {
          if (startKind === 'person' && fromPerson > 0) setCreated(role)
          else onCreated(role)
        },
        onError: (error: unknown) => setFailure(apiErrorMessage(error, tt('Rolu sa nepodarilo vytvoriť.'))),
      },
    )
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-role-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={busy ? undefined : close}
    >
      <div
        ref={panelRef}
        onKeyDown={onKeyDown}
        onClick={(event) => event.stopPropagation()}
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-4"
        data-new-role-dialog={created === null ? 'form' : 'move'}
      >
        <h2 id="new-role-title" className="text-lg font-bold">
          {created === null ? tt('Nová rola') : `${tt('Rola vytvorená')}: ${created.name ?? ''}`}
        </h2>

        {created === null ? (
          <>
            <label className="block space-y-1">
              <span className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                {tt('Názov')}
              </span>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={40}
                placeholder={tt('napríklad Vedúci smeny')}
                className="w-full px-3 py-2 rounded-xl bg-background border border-border text-sm"
                data-new-role-name
              />
            </label>
            {name.trim() !== '' && problem !== null && (
              <p className="text-xs text-red-500" data-new-role-problem={problem}>
                {roleNameProblemText(problem)}
              </p>
            )}

            <div className="space-y-1.5">
              <span className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                {tt('Pracuje ako')}
              </span>
              <p className="text-[11px] text-muted-foreground">
                <T>Základ sa vyberá raz a už sa nemení - podľa neho sa rola správa všade, kde program rozhoduje podľa roly.</T>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ROLE_BASES.map((candidate) => (
                  <button
                    key={candidate}
                    type="button"
                    onClick={() => setBase(candidate)}
                    aria-pressed={base === candidate}
                    data-new-role-base={candidate}
                    className={cn(
                      'rounded-xl border p-3 text-left space-y-1 transition-colors',
                      base === candidate ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/50',
                    )}
                  >
                    <span className="block text-sm font-bold">{roleLabel(candidate)}</span>
                    <span className="block text-[11px] text-muted-foreground">
                      {baseSentence(candidate, overview.roleListReach[candidate])}
                    </span>
                    {candidate === 'admin' && (
                      <span className="flex items-start gap-1 text-[11px] text-amber-500" data-new-role-admin-warning>
                        <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                        <span>
                          {roleWarnings({ key: 'new', baseRole: 'admin', administratorPass: false }, overview.roleListReach)
                            .map((warning) => warning.text)
                            .join(' ')}{' '}
                          {tt('Takú rolu prideľuje len hlavný administrátor.')}
                        </span>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <span className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                {tt('Začať od')}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {(
                  [
                    ['defaults', tt('Odporúčané')],
                    ['role', tt('Kópia roly...')],
                    ['person', tt('Práva človeka...')],
                    ['empty', tt('Prázdna')],
                  ] as const
                ).map(([kind, label]) => (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => setStartKind(kind)}
                    aria-pressed={startKind === kind}
                    data-new-role-start={kind}
                    className={cn(
                      'px-2.5 py-1.5 rounded-lg border text-[11px] font-bold transition-colors',
                      startKind === kind
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'border-border text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {startKind === 'defaults' && (
                <p className="text-[11px] text-muted-foreground">
                  {base === null
                    ? tt('Odporúčané práva podľa základu, ktorý vyberiete.')
                    : `${tt('Odporúčané práva pre základ')} ${roleLabel(base)}.`}
                </p>
              )}
              {startKind === 'role' && (
                <select
                  value={fromRole}
                  onChange={(event) => setFromRole(event.target.value)}
                  aria-label={tt('Rola, ktorej práva sa skopírujú')}
                  className="w-full px-3 py-2 rounded-xl bg-background border border-border text-sm"
                  data-new-role-from-role
                >
                  <option value="">{tt('Vyberte rolu')}</option>
                  {roles.map((role) => (
                    <option key={role.key} value={role.key}>
                      {roleNameOf(overview.roles, role.key)}
                    </option>
                  ))}
                </select>
              )}
              {startKind === 'person' && (
                <>
                  <select
                    value={fromPerson === 0 ? '' : String(fromPerson)}
                    onChange={(event) => setFromPerson(Number(event.target.value) || 0)}
                    aria-label={tt('Človek, ktorého práva sa skopírujú')}
                    className="w-full px-3 py-2 rounded-xl bg-background border border-border text-sm"
                    data-new-role-from-person
                  >
                    <option value="">{tt('Vyberte človeka')}</option>
                    {people.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.name}
                        {(person.position ?? '').trim() === '' ? '' : ` - ${person.position ?? ''}`}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-muted-foreground">
                    <T>Skopíruje sa jeho menu - práva roly a udelené výnimky bez zakázaných. Nikdy nie všetko.</T>
                  </p>
                </>
              )}
            </div>

            <ColorPicker value={color} onChange={setColor} />

            <label className="block space-y-1">
              <span className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                {tt('Popis')}
              </span>
              <textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                maxLength={200}
                rows={2}
                className="w-full px-3 py-2 rounded-xl bg-background border border-border text-sm"
                data-new-role-description
              />
            </label>

            {failure !== '' && (
              <p className="text-xs text-red-500" role="alert" data-new-role-error>
                {failure}
              </p>
            )}
          </>
        ) : (
          <MoveOffer
            role={created}
            personId={fromPerson}
            overview={overview}
            moving={moving}
            setMoving={setMoving}
            onDone={() => onCreated(created)}
          />
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={close}
            disabled={busy}
            data-dialog-cancel
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            {created === null ? tt('Zrušiť') : tt('Teraz nie')}
          </button>
          {created === null && (
            <button
              type="button"
              onClick={submit}
              disabled={!ready}
              className={cn(BUTTON_PRIMARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
              data-new-role-create
            >
              {create.isPending ? tt('Spracúvam...') : tt('Vytvoriť rolu')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * The second step of *Nová rola* started from a person - *Presunúť {meno} do novej roly* with what it would do.
 *
 * The move is the server's assignment (`PUT /employees/{id}/role`) and is refused there by the same rules the options
 * answer here; the exceptions it clears go through the existing endpoint, after the move and only if it succeeded.
 */
function MoveOffer({
  role,
  personId,
  overview,
  moving,
  setMoving,
  onDone,
}: {
  role: RoleView
  personId: number
  overview: RolesOverview
  moving: boolean
  setMoving: (on: boolean) => void
  onDone: () => void
}) {
  const { toast } = useToast()
  const options = useRoleOptions(personId)
  const rights = useGetSettingsPermissionsEmployees()
  const assign = useAssignRole()
  const saveOverrides = usePutSettingsPermissionsEmployeeId()
  const [clearCovered, setClearCovered] = useState(true)

  const person = overview.people.find((candidate) => candidate.id === personId)
  const catalogue = overview.allPermissions
  const known = useMemo(() => new Set(catalogue), [catalogue])
  const labelOf = (key: string) => permissionLabel(key, displayParentOf(key, known))
  const row = rights.data?.employees.find((candidate) => candidate.id === personId)
  const overrides = row?.overrides ?? []
  const covered = grantsTheRoleCovers(overrides, role.permissions)
  const option = options.data?.options.find((candidate) => candidate.key === role.key)
  const fromBase = person?.systemRole ?? ''

  if (person === undefined) return null
  if (options.isError) {
    return (
      <p className="text-xs text-red-500" role="alert" data-move-offer-failed>
        {tt('Nepodarilo sa zistiť, čo by presun urobil - človeka presuňte na karte Ľudia.')}
      </p>
    )
  }
  if (options.isLoading || option === undefined) {
    return (
      <div className="flex justify-center py-4">
        <Spinner />
      </div>
    )
  }

  const consequences = optionConsequences(option, {
    personName: person.name,
    fromBase,
    toBase: role.baseRole,
    serverChecked: new Set(overview.serverChecked),
    labelOf,
  })

  const move = () => {
    setMoving(true)
    assign.mutate(
      { id: personId, data: { roleKey: role.key, expectedRoleKey: options.data?.current ?? null } },
      {
        onSuccess: () => {
          if (!clearCovered || covered.length === 0 || rights.isError) {
            setMoving(false)
            toast({ title: `${person.name} ${tt('má teraz rolu')} ${role.name ?? ''}` })
            onDone()
            return
          }
          saveOverrides.mutate(
            { id: personId, data: overridePayload(withoutCoveredGrants(overrides, role.permissions), catalogue) },
            {
              onSuccess: () => {
                setMoving(false)
                toast({ title: `${person.name} ${tt('má teraz rolu')} ${role.name ?? ''}` })
                onDone()
              },
              onError: (error: unknown) => {
                setMoving(false)
                toast({
                  title: tt('Presunuté, ale výnimky ostali'),
                  description: apiErrorMessage(error, tt('Výnimky sa nepodarilo zrušiť.')),
                  variant: 'destructive',
                })
                onDone()
              },
            },
          )
        },
        onError: (error: unknown) => {
          setMoving(false)
          toast({
            title: tt('Chyba'),
            description: apiErrorMessage(error, tt('Rolu sa nepodarilo prideliť.')),
            variant: 'destructive',
          })
        },
      },
    )
  }

  return (
    <div className="space-y-2" data-move-offer={personId}>
      <p className="text-sm">
        {tt('Presunúť')} <strong>{person.name}</strong> {tt('do novej roly?')}
      </p>
      {!option.allowed ? (
        <p className="text-xs text-red-500" data-move-refused={option.code ?? ''}>
          {refusalText(option.code)}
        </p>
      ) : (
        <ul className="space-y-1">
          {consequences.map((consequence) => (
            <li
              key={consequence.kind}
              className={cn(
                'text-xs',
                consequence.kind === 'signs-out' || consequence.kind === 'server-stops'
                  ? 'text-amber-500'
                  : 'text-muted-foreground',
              )}
              data-move-consequence={consequence.kind}
            >
              {consequence.text}
            </li>
          ))}
        </ul>
      )}
      {option.allowed && covered.length > 0 && (
        <label className="flex items-start gap-2 text-xs" data-move-clear-covered>
          <input
            type="checkbox"
            checked={clearCovered}
            onChange={(event) => setClearCovered(event.target.checked)}
            className="mt-0.5"
          />
          <span>
            {tt('Zrušiť výnimky, ktoré nová rola už obsahuje')}: {covered.map(labelOf).join(', ')}
          </span>
        </label>
      )}
      {option.allowed && (
        <button
          type="button"
          onClick={move}
          disabled={moving}
          className={cn(BUTTON_PRIMARY, 'px-4 py-2 rounded-xl text-sm')}
          data-move-confirm
        >
          {moving ? tt('Spracúvam...') : `${tt('Presunúť')} ${person.name}`}
        </button>
      )}
    </div>
  )
}
